<script setup lang="ts">
import Dialog from '../Dialog/Dialog.vue';
import { useToggle } from '@vueuse/core';
import { DialogProps } from '../Dialog/types';

interface ConfirmedActionProps extends DialogProps {
  needsConfirmation?: boolean;
  persistent?: boolean;
}

const props = withDefaults(defineProps<ConfirmedActionProps>(), {
  needsConfirmation: () => true,
  persistent: true,
});

type ConfirmationAnswer = 'confirmed' | 'noConfirmationNeeded';

const emits = defineEmits<{
  confirmed: [answer: ConfirmationAnswer];
  cancel: [];
}>();

const [isDialogOpen, toggleDialogOpen] = useToggle(false);

async function confirmedAction(forceNeedsConfirmation = false) {
  if (props.needsConfirmation || forceNeedsConfirmation) {
    toggleDialogOpen(true);
  } else {
    emits('confirmed', 'noConfirmationNeeded');
  }
}

async function confirm() {
  toggleDialogOpen(false);
  emits('confirmed', 'confirmed');
}

function cancel() {
  toggleDialogOpen(false);
  emits('cancel');
}

function onDialogClose() {
  cancel();
}
</script>

<template>
  <slot name="default" :confirmedAction="confirmedAction" />

  <slot
    name="dialog"
    :isDialogOpen="isDialogOpen"
    :toggleDialogOpen="toggleDialogOpen"
    :confirm="confirm"
    :cancel="cancel"
  >
    <Dialog
      v-bind="props"
      :hideClose="true"
      :modelValue="isDialogOpen"
      @update:modelValue="onDialogClose"
    >
      <template #title>
        <slot
          name="dialog.title"
          :confirm="confirm"
          :cancel="cancel"
          :confirmedAction="confirmedAction"
        >
          {{ $t('Confirmation') }}
        </slot>
      </template>

      <template #text>
        <slot
          name="dialog.text"
          :confirm="confirm"
          :cancel="cancel"
          :confirmedAction="confirmedAction"
        />
      </template>

      <template #actions>
        <slot
          name="dialog.actions"
          :confirm="confirm"
          :cancel="cancel"
          :confirmedAction="confirmedAction"
        >
          <TextVBtn @click="cancel">
            {{ $t('Cancel') }}
          </TextVBtn>

          <SpecialVBtn @click="confirm">
            {{ $t('Confirm') }}
          </SpecialVBtn>
        </slot>
      </template>
    </Dialog>
  </slot>
</template>
